/**
 * useAssistantChat — streaming chat for the unified page-aware assistant.
 *
 * Uses `useChatCore` over `DefaultChatTransport`,
 * user turns batch-persisted with a clientMessageId, assistant turns
 * persisted server-side, `useMessageSync` seeding threads. Differences:
 *  - conversations are org-shared `assistant` threads; the active one is
 *    pinned per browser session (sessionStorage) so it survives navigation;
 *  - every request carries fresh `pageContext` (read from a ref at send
 *    time — the transport is memoized on the conversation only);
 *  - route changes append persisted "Navigated to X" system markers.
 */

import React from 'react';
import { useRouter } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls } from 'ai';
import type { UIMessage } from 'ai';
import { useToast } from '@chatsdet/ui';
import { useGuide } from '@runcofactor/guide';
import { useChatCore, convertPersistedToUIMessage, useMessageSync } from '../../hooks/chat';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type { ConversationMessage } from '../../hooks/useConversations';
import { usePageContext } from './usePageContext';
import { buildLocation, resolvePageContext, type ResolvedPageContext } from './pageRegistry';
import { toGuideOp, toToolOutput } from './guideOp';
import {
  resolveCapabilityInvalidation,
  ASSISTANT_ACTIVITIES_QUERY_KEY,
} from './capabilityInvalidation';
import { assistantOnboarding, CAPABILITY_TOOL_STREAM_PART_TYPE } from '@chatsdet/types';
import type { AssistantOnboardingId } from '@chatsdet/types';
import { matchesFinish } from './onboardingFlow';
import { useInvalidateProductMap } from '../product-map/useProductMap';

/** An onboarding running against a specific conversation — see `onboardingFlow.ts`. */
export type ActiveOnboarding = {
  id: AssistantOnboardingId;
  params: Record<string, string> | undefined;
  conversationId: string;
};

const SESSION_PIN_KEY = 'chatsdet_assistant_conversation_id';
const NAVIGATION_DEBOUNCE_MS = 1200;

function generateId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {}
  // Fallback for non-secure contexts (http) where crypto.randomUUID is unavailable
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Each round trip is a separate HTTP request and a separate server-side
 * `streamText`, so the server's `stopWhen: stepCountIs(24)` resets every time
 * and bounds nothing across round trips — this is the client-side backstop.
 */
/** Identity of a page for the "Navigated to …" marker: page plus its entity params. */
const pageHintKey = (page: ResolvedPageContext) => `${page.pageId}:${JSON.stringify(page.params)}`;

/**
 * Yield until React has committed a render and flushed its passive effects.
 * Two macrotasks: the first lets the commit that follows a router update
 * happen, the second lets `useEffect` work scheduled by that commit run.
 *
 * This is a timing heuristic, not a signal. It holds because no route is
 * code-split and every page's `useGuideSurface` declarations mount
 * unconditionally with the page — the first lazy route would be the thing
 * that breaks it, and the symptom would be a resubmit whose `guide.surfaces`
 * belong to the page just left. `requestAnimationFrame` is not used because
 * it does not fire in a background tab, which would hang the tool call.
 */
const settleRender = () =>
  new Promise<void>((resolve) => setTimeout(() => setTimeout(resolve, 0), 0));

const MAX_PAGE_UI_ROUND_TRIPS = 30;
const ROUND_TRIP_LIMIT_MESSAGE =
  'You have reached the round-trip limit for this turn. Stop driving the page now and summarize for the user what you observed and accomplished.';

export type AssistantUIMessage = UIMessage;

// -----------------------------------------------------------------------------
// Session pin
// -----------------------------------------------------------------------------

function readSessionPin(): string | null {
  try {
    return sessionStorage.getItem(SESSION_PIN_KEY);
  } catch {
    return null;
  }
}

function writeSessionPin(conversationId: string): void {
  try {
    sessionStorage.setItem(SESSION_PIN_KEY, conversationId);
  } catch {
    // Session pinning is best-effort; a fresh default chat is minted next open.
  }
}

// -----------------------------------------------------------------------------
// API helpers
// -----------------------------------------------------------------------------

async function ensureConversation(conversationId: string): Promise<string> {
  const res = await fetch(`${API_URL}/conversations/get-or-create`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: conversationId, entityType: 'assistant', title: 'Assistant' }),
  });
  if (!res.ok) throw await toApiError(res, 'Failed to open assistant conversation');
  const json = (await res.json()) as { ok: boolean; data: { id: string } };
  return json.data.id;
}

async function fetchMessages(conversationId: string): Promise<ConversationMessage[]> {
  const res = await fetch(`${API_URL}/conversations/${conversationId}/messages?pageSize=200`, {
    credentials: 'include',
  });
  if (!res.ok) throw await toApiError(res, 'Failed to fetch messages');
  const json = (await res.json()) as { ok: boolean; data: ConversationMessage[] };
  return json.data;
}

async function persistMessages(
  conversationId: string,
  messages: Array<{
    clientMessageId?: string;
    role: 'user' | 'assistant' | 'system' | 'tool';
    content?: string | null;
    parts?: unknown[] | null;
    metadata?: Record<string, unknown> | null;
  }>
): Promise<void> {
  const res = await fetch(`${API_URL}/conversations/${conversationId}/messages/batch`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => 'unknown');
    throw new Error(`Persist messages failed (${res.status}): ${text}`);
  }
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export function useAssistantChat(options: {
  enabled: boolean;
  panelOpen: boolean;
  /** An onboarding running against the CURRENT conversation, or none. */
  onboarding?: ActiveOnboarding | null;
  /** Fires with the finish capability's `result` once the onboarding succeeds. */
  onOnboardingFinished?: (result: unknown) => void;
  /**
   * Extra `pageContext.params` merged in for a plain "thread with kickoff"
   * or "ask" (see `onboardingFlow.ts`'s `KickoffState`/`AskState`) — only
   * while a candidate's `conversationId` matches the live conversation,
   * mirroring the onboarding overlay below. Unlike onboarding params, these
   * are NOT wrapped in an `onboarding: { id }` marker — neither kickoff nor
   * ask has a finish capability to key on.
   *
   * Several pins can be live at once — a kickoff thread and an ask thread
   * both mint their own conversation, and neither mint retires the other —
   * so this takes ALL candidates rather than one pre-selected pin. Matching
   * on `conversationId` against the live `conversationId` (below) is what
   * makes the selection exclusive: at most one candidate can match a given
   * conversation, and a stale candidate for a different (or since-abandoned)
   * conversation is simply ignored rather than shadowing the live one.
   */
  threadPins?: ReadonlyArray<{ conversationId: string; params: Record<string, string> }>;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const pageContext = usePageContext();
  const guide = useGuide();
  const router = useRouter();

  const onboardingRef = React.useRef(options.onboarding ?? null);
  onboardingRef.current = options.onboarding ?? null;
  const onOnboardingFinishedRef = React.useRef(options.onOnboardingFinished);
  onOnboardingFinishedRef.current = options.onOnboardingFinished;
  const threadPinsRef = React.useRef(options.threadPins ?? []);
  threadPinsRef.current = options.threadPins ?? [];

  // `onToolCall` below is wired into the underlying `Chat` instance ONCE, at
  // whatever render first constructs it for this `chatId` (see
  // `useChatCore`'s comment) — so it must never close over a render-scoped
  // value directly. Everything it needs is read through a ref instead.
  const guideRef = React.useRef(guide);
  guideRef.current = guide;
  const routerRef = React.useRef(router);
  routerRef.current = router;

  // Bounds `page_ui` round trips for the CURRENT user turn. Reset in
  // `beforeSend`, below.
  const roundTripCountRef = React.useRef(0);

  // Active conversation: session pin, or a freshly minted default chat.
  const [activeConversationId, setActiveConversationId] = React.useState<string>(
    () => readSessionPin() ?? generateId()
  );

  const conversationQuery = useQuery({
    queryKey: ['assistant-conversation', activeConversationId],
    queryFn: async () => {
      const id = await ensureConversation(activeConversationId);
      writeSessionPin(id);
      return id;
    },
    enabled: options.enabled,
    staleTime: Infinity,
  });
  const conversationId = conversationQuery.data ?? null;

  const conversationIdRef = React.useRef(conversationId);
  conversationIdRef.current = conversationId;

  // Fresh page context at send time without rebuilding the transport.
  const pageContextRef = React.useRef<ResolvedPageContext>(pageContext);
  pageContextRef.current = pageContext;

  const messagesQuery = useQuery({
    queryKey: ['assistant-messages', conversationId],
    queryFn: () => fetchMessages(conversationId!),
    enabled: !!conversationId,
    staleTime: 30_000,
  });

  const initialMessages = React.useMemo(
    () =>
      (messagesQuery.data ?? []).map((msg) =>
        convertPersistedToUIMessage<AssistantUIMessage>(msg, (persisted) => ({
          ...(persisted.metadata ?? {}),
          createdAt: persisted.createdAt,
        }))
      ),
    [messagesQuery.data]
  );

  const transport = React.useMemo(
    () =>
      conversationId
        ? new DefaultChatTransport<AssistantUIMessage>({
            api: `${API_URL}/assistant/chat/messages`,
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', 'X-Conversation-Id': conversationId },
            body: () => {
              // The declared index only — live `describe()` state is not sent
              // here; it travels back through a `page_ui` tool result instead
              // (see `AssistantPageContextSchema`'s comment).
              const surfaces = guideRef.current.registry.list().map((surface) => ({
                id: surface.id,
                role: surface.role,
                label: surface.label,
                // Carried too: a label says what a control is called, a
                // description says why you would touch it, and the index is
                // where the model does its planning before it ever calls a tool.
                ...(surface.description ? { description: surface.description } : {}),
              }));
              // Only merged in while the onboarding is running against THIS
              // conversation — a stale `onboardingRef` (the user switched
              // threads or started a new chat mid-onboarding) must not leak
              // its params or its capability set into an unrelated thread.
              const onboarding =
                onboardingRef.current?.conversationId === conversationId
                  ? onboardingRef.current
                  : null;
              // Same "only while it's THIS conversation" guard as onboarding
              // params, above — a thread kickoff/ask carries no finish
              // capability, so it's merged in plainly (no `onboarding: { id }`
              // wrapper) rather than treated as a capability-gated overlay.
              // Matching on `conversationId` against ALL candidate pins (not
              // one pre-selected by the caller) is what makes this exclusive
              // — see the `threadPins` option doc above.
              const thread =
                threadPinsRef.current.find((pin) => pin.conversationId === conversationId) ?? null;
              return {
                pageContext: {
                  pageId: pageContextRef.current.pageId,
                  pathname: pageContextRef.current.pathname,
                  params: {
                    ...pageContextRef.current.params,
                    ...thread?.params,
                    ...onboarding?.params,
                  },
                  // Absent (not empty) when nothing is declared: presence is
                  // what puts `page_ui` in the model's toolset server-side.
                  ...(surfaces.length > 0 ? { guide: { surfaces } } : {}),
                  ...(onboarding ? { onboarding: { id: onboarding.id } } : {}),
                },
              };
            },
          })
        : null,
    [conversationId]
  );

  // Resolve one client-executed tool call — `page_ui` (drives the page
  // through the guide) or `navigate` (moves the router) — and ALWAYS resolve
  // it: an unresolved tool part leaves
  // `lastAssistantMessageIsCompleteWithToolCalls` false forever and wedges the
  // thread (see `validateChatTurn` server-side).
  //
  // Stable identity ([] deps): only the render that first constructs the
  // underlying `Chat` instance for this `chatId` has its `onToolCall` wired in
  // (see `useChatCore`), so everything here is read live through refs rather
  // than closed over.
  const handleClientToolCall = React.useCallback<
    NonNullable<Parameters<typeof useChatCore<AssistantUIMessage>>[0]['onToolCall']>
  >(async ({ toolCall, addToolOutput }) => {
    if (toolCall.toolName === 'navigate') {
      // Shares the per-turn round-trip budget with `page_ui`. Past the budget
      // every navigate is refused with an error — the page stops moving — but
      // each refusal still resolves and resubmits, so the number of resubmits
      // is bounded by the model stopping, exactly as it is for `page_ui`
      // (whose latch stops the driving, not the resubmitting). It does NOT
      // touch the guide session: a page change is not a drive step, and
      // closing the session here would refuse the model's next legitimate
      // `page_ui` call on the new page.
      try {
        roundTripCountRef.current += 1;
        if (roundTripCountRef.current > MAX_PAGE_UI_ROUND_TRIPS) {
          addToolOutput({
            tool: 'navigate',
            toolCallId: toolCall.toolCallId,
            state: 'output-error',
            errorText: ROUND_TRIP_LIMIT_MESSAGE,
          });
          return;
        }
        const input = toolCall.input as {
          pageId?: unknown;
          params?: unknown;
        };
        if (typeof input.pageId !== 'string') {
          throw new Error('navigate call arrived with no pageId');
        }
        const params: Record<string, string> = {};
        if (input.params !== undefined) {
          if (typeof input.params !== 'object' || input.params === null) {
            throw new Error('navigate params must be an object of strings');
          }
          for (const [key, value] of Object.entries(input.params as Record<string, unknown>)) {
            if (typeof value !== 'string') {
              throw new Error(`navigate params.${key} must be a string`);
            }
            params[key] = value;
          }
        }

        await routerRef.current.navigate(buildLocation(input.pageId, params));

        // The SDK resubmits the moment this call resolves, and the request
        // body is read from refs at that instant. Two things have to have
        // happened by then, and neither has when `navigate` returns: the
        // router's `matches` must have committed (its `location` updates
        // first — see the note in usePageContext.ts), and the new page's
        // `useGuideSurface` effects must have registered its controls.
        // Both land within a render and its passive effects, so yield two
        // macrotasks before reading anything — long enough for React to
        // commit and flush, and unlike `requestAnimationFrame` it still
        // fires in a background tab.
        await settleRender();

        const routerState = routerRef.current.state;
        const destination = resolvePageContext(
          routerState.matches.map((match) => ({
            routeId: String(match.routeId),
            params: match.params as Record<string, unknown>,
          })),
          routerState.location.pathname,
          (routerState.location.search ?? {}) as Record<string, unknown>
        );

        // Publish the destination into the ref the transport reads, rather
        // than trusting that the render above updated it, and record it as
        // the last seen page so the route-change effect below does not append
        // a second "Navigated to …" marker for a move the assistant itself
        // announced and already knows about.
        pageContextRef.current = destination;
        lastHintKeyRef.current = pageHintKey(destination);

        addToolOutput({
          tool: 'navigate',
          toolCallId: toolCall.toolCallId,
          output: `Now on ${destination.label} (${destination.pathname})`,
        });
      } catch (error) {
        addToolOutput({
          tool: 'navigate',
          toolCallId: toolCall.toolCallId,
          state: 'output-error',
          errorText: error instanceof Error ? error.message : 'The navigate call failed.',
        });
      }
      return;
    }

    if (toolCall.toolName !== 'page_ui') return;

    try {
      roundTripCountRef.current += 1;
      if (roundTripCountRef.current > MAX_PAGE_UI_ROUND_TRIPS) {
        // Close the session AND resolve with an error, in that order.
        //
        // Declining alone only stops the page being driven: the model can keep
        // calling, `sendAutomaticallyWhen` keeps resubmitting, and the round
        // trips this budget exists to bound continue unbounded. Closing the
        // session makes every later call a refusal from the driver itself.
        //
        // An error output rather than silence, so the model gets one more turn
        // in which to stop and summarize — refusing to resubmit would stall the
        // turn with no explanation.
        guideRef.current.stop();
        addToolOutput({
          tool: 'page_ui',
          toolCallId: toolCall.toolCallId,
          state: 'output-error',
          errorText: ROUND_TRIP_LIMIT_MESSAGE,
        });
        return;
      }

      const op = toGuideOp(toolCall.input);
      const result = await guideRef.current.execute(op);
      addToolOutput({
        tool: 'page_ui',
        toolCallId: toolCall.toolCallId,
        output: toToolOutput(result),
      });
    } catch (error) {
      // Every throwing path lands here — `toGuideOp` on a malformed call, and
      // anything escaping `execute`. Both branches resolve the call, which is
      // the invariant that matters.
      addToolOutput({
        tool: 'page_ui',
        toolCallId: toolCall.toolCallId,
        state: 'output-error',
        errorText: error instanceof Error ? error.message : 'The page_ui operation failed.',
      });
    }
  }, []);

  const core = useChatCore<AssistantUIMessage>({
    chatId: `assistant-${conversationId ?? 'none'}`,
    transport,
    beforeSend: async (text, metadata) => {
      // A fresh user turn starts a fresh round-trip budget, and hands the
      // controls back.
      //
      // A guide session spans ONE user turn: opened here, closed in `onFinish`.
      // That is what reconciles two things that otherwise conflict — Escape has
      // to keep working while the model is thinking between operations, but a
      // session left open across turns would keep the cursor on screen and, via
      // `externalBusy`, keep the composer disabled forever. Bounding it to the
      // turn also makes the takeover latch mean the right thing: sending a new
      // message IS the user handing control back.
      roundTripCountRef.current = 0;
      guideRef.current.start();
      const cid = conversationIdRef.current;
      if (!cid) return;
      const clientMessageId = generateId();
      void persistMessages(cid, [
        {
          clientMessageId,
          role: 'user',
          content: text,
          parts: [{ type: 'text', text }],
          ...(metadata ? { metadata } : {}),
        },
      ]).catch((err) => console.error('Failed to persist assistant user message', err));
    },
    onFinish: ({ finishReason }) => {
      // A client-tool turn finishes once per ROUND TRIP. `'tool-calls'` means
      // `sendAutomaticallyWhen` is about to resubmit, so the guide session must
      // stay open — closing it here would latch it shut and the very next tool
      // call would be refused as "the user took the controls back".
      if (finishReason !== 'tool-calls') {
        guideRef.current.stop();
      }
      const cid = conversationIdRef.current;
      if (!cid) return;
      // Assistant turn is persisted server-side; refetch so the persisted row
      // replaces the live message on reload/thread-switch.
      void queryClient.invalidateQueries({ queryKey: ['assistant-messages', cid] });
    },
    onError: () => {
      // Close the session too: an errored turn must not leave the cursor on
      // screen with the composer disabled.
      guideRef.current.stop();
      toast.error('The assistant hit a problem answering. Try again.', { reportError: false });
    },
    onToolCall: handleClientToolCall,
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    // Between round trips `useChat.status` goes back to `ready` — without
    // this the composer would re-enable and the Stop button vanish mid-drive.
    externalBusy: guide.isDriving,
  });

  useMessageSync({
    conversationId,
    initialMessages,
    setMessages: core.setMessages,
    currentMessageCount: core.messages.length,
  });

  // ---------------------------------------------------------------------------
  // Domain refresh: invalidate the query keys implied by an executed
  // capability, so a write made through the assistant appears on screen
  // without a reload (plan finding 1). Deliberately NOT `onToolCall` above —
  // that callback only fires for `page_ui` (it returns early for anything
  // else) and only carries the CLIENT-DRIVEN page-guide loop, never a
  // server-executed `capabilities` result. Streamed `capabilities` tool parts
  // land in `core.messages` instead, so that is what this observes.
  // ---------------------------------------------------------------------------
  const invalidateProductMap = useInvalidateProductMap();
  const processedCapabilityToolCallIdsRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    for (const message of core.messages) {
      for (const part of message.parts) {
        if (!part || typeof part !== 'object') continue;

        const type = (part as { type?: unknown }).type;
        if (type !== CAPABILITY_TOOL_STREAM_PART_TYPE) continue;

        const state = (part as { state?: unknown }).state;
        if (state !== 'output-available') continue;

        const toolCallId = (part as { toolCallId?: unknown }).toolCallId;
        if (typeof toolCallId !== 'string') continue;
        if (processedCapabilityToolCallIdsRef.current.has(toolCallId)) continue;
        processedCapabilityToolCallIdsRef.current.add(toolCallId);

        const output = (part as { output?: unknown }).output;
        for (const target of resolveCapabilityInvalidation(output, pageContext.params)) {
          if (target.kind === 'product-map') {
            void invalidateProductMap();
          } else if (target.kind === 'assistant-activities') {
            const cid = conversationIdRef.current;
            if (cid) {
              void queryClient.invalidateQueries({
                queryKey: [...ASSISTANT_ACTIVITIES_QUERY_KEY, cid],
              });
            }
          } else if (target.kind === 'exploration') {
            void queryClient.invalidateQueries({ queryKey: ['exploration-cycles'] });
            void queryClient.invalidateQueries({ queryKey: ['exploration-schedules'] });
          } else {
            void queryClient.invalidateQueries({
              queryKey: ['workflow-detail', target.workflowId],
            });
          }
        }

        // An active onboarding ends the moment ITS finish capability
        // succeeds — never on some other capability's success, and never on
        // an execution failure (the assistant needs the chance to retry).
        const onboarding = onboardingRef.current;
        if (
          onboarding &&
          onboarding.conversationId === conversationIdRef.current &&
          matchesFinish(onboarding, assistantOnboarding(onboarding.id).finishCapabilityId, output)
        ) {
          onOnboardingFinishedRef.current?.(output.result);
        }
      }
    }
  }, [core.messages, pageContext.params, invalidateProductMap, queryClient]);

  // ---------------------------------------------------------------------------
  // Navigation hints: persist a system marker when the page changes while the
  // panel is open and the conversation has content. Debounced so rapid A→B→C
  // navigation records only the destination; consecutive same-page hints
  // collapse.
  // ---------------------------------------------------------------------------
  const lastHintKeyRef = React.useRef<string | null>(null);
  const hasUserMessage = core.messages.some((message) => message.role === 'user');
  const { setMessages } = core;

  // Mirrored so `reloadMessages` can re-check it after an await without taking
  // a dependency that would make the callback change identity every turn.
  const isSendingRef = React.useRef(core.isSending);
  isSendingRef.current = core.isSending;
  const messagesRef = React.useRef(core.messages);
  messagesRef.current = core.messages;

  // Shared by the navigation hint below and `announceAttachment`: live-append
  // a system marker (renders instantly as a divider) and persist it for
  // reloads and other org members viewing the shared thread. The server
  // rewrites any system message carrying `metadata.kind` into a bracketed
  // user turn, so the model sees it without further plumbing.
  const appendMarker = React.useCallback(
    (text: string, metadata: Record<string, unknown>) => {
      const cid = conversationIdRef.current;
      if (!cid) return;
      const next = [
        ...messagesRef.current,
        {
          id: `marker-${generateId()}`,
          role: 'system',
          parts: [{ type: 'text', text }],
          metadata,
        } as AssistantUIMessage,
      ];
      // Write the ref forward, not just the state. `messagesRef` is refreshed
      // on render and `setMessages` takes an array rather than an updater, so
      // without this a multi-file upload — which announces once per file in a
      // single tick — would have every marker but the last read the same stale
      // array and be dropped from the live view.
      messagesRef.current = next;
      setMessages(next);
      void persistMessages(cid, [
        { role: 'system', content: text, parts: [{ type: 'text', text }], metadata },
      ]).catch((err) => console.error('Failed to persist assistant marker', err));
    },
    [setMessages]
  );

  /** Announce a newly attached context document with a `context-attached` marker. */
  const announceAttachment = React.useCallback(
    (fileName: string) => {
      appendMarker(`Attached "${fileName}"`, { kind: 'context-attached', fileName });
    },
    [appendMarker]
  );

  React.useEffect(() => {
    if (!options.panelOpen || !conversationId || !hasUserMessage) return;
    if (pageContext.pageId === 'unknown') return;

    const hintKey = pageHintKey(pageContext);
    if (lastHintKeyRef.current === null) {
      // First observation for this conversation/session — record silently so
      // an initial mount doesn't produce a "Navigated" marker.
      lastHintKeyRef.current = hintKey;
      return;
    }
    if (lastHintKeyRef.current === hintKey) return;

    const timer = setTimeout(() => {
      // Re-check at fire time: an assistant `navigate` records its destination
      // in the ref AFTER this effect has already armed the timer (the render
      // that runs this effect lands inside the tool's settle wait), and its
      // own tool chip already announces the move.
      if (lastHintKeyRef.current === hintKey) return;
      lastHintKeyRef.current = hintKey;
      appendMarker(`Navigated to ${pageContext.label}`, {
        kind: 'navigation',
        pageId: pageContext.pageId,
        pathname: pageContext.pathname,
        params: pageContext.params,
      });
    }, NAVIGATION_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [
    options.panelOpen,
    conversationId,
    hasUserMessage,
    pageContext.pageId,
    pageContext.pathname,
    pageContext.label,
    pageContext.params,
    appendMarker,
  ]);

  // ---------------------------------------------------------------------------
  // Thread switching
  // ---------------------------------------------------------------------------
  const switchConversation = React.useCallback((nextConversationId: string) => {
    lastHintKeyRef.current = null;
    writeSessionPin(nextConversationId);
    setActiveConversationId(nextConversationId);
  }, []);

  /** Mint and switch to a fresh conversation, returning its id. */
  const startNewChat = React.useCallback(() => {
    const id = generateId();
    switchConversation(id);
    return id;
  }, [switchConversation]);

  /**
   * Send a user turn the Host constructs rather than the composer — a hidden
   * onboarding/thread kickoff (carrying `metadata` like
   * `{ kind: ONBOARDING_KICKOFF_MARKER_KIND, onboardingId }` so the panel
   * renders it as a divider) or a plain visible turn into a fresh thread
   * (`metadata: {}`, so it renders as an ordinary bubble — see `askAssistant`
   * in `AssistantContext.tsx`). Goes through `core.sendText` so it takes
   * exactly the same pre-send path as a typed message — persistence,
   * round-trip budget reset, guide session — just with different text and
   * metadata than the composer would have supplied.
   */
  const sendUserTurn = React.useCallback(
    async (text: string, metadata: Record<string, unknown>) => {
      try {
        await core.sendText(text, metadata);
      } catch {
        // `sendText` already reported the failure through `onError`'s toast;
        // swallow here so the Host's fire-and-forget call never surfaces as
        // an unhandled rejection.
      }
    },
    [core.sendText]
  );

  /**
   * Re-seed the visible thread from persisted history.
   *
   * `useMessageSync` seeds a conversation once and then deliberately refuses to
   * re-seed, so an in-flight stream is never clobbered by a late refetch. That
   * guard also means a server-authored turn — the assistant reporting finished
   * background work — refetches into the query cache but never reaches the
   * screen. This is the explicit escape hatch for that case.
   *
   * Refuses while a turn is streaming, for the same reason the guard exists.
   */
  const reloadMessages = React.useCallback(async () => {
    const cid = conversationIdRef.current;
    if (!cid || isSendingRef.current) return;

    const persisted = await queryClient.fetchQuery({
      queryKey: ['assistant-messages', cid],
      queryFn: () => fetchMessages(cid),
    });

    // Re-check AFTER the await, not just before it. The fetch is a real round
    // trip whenever the cache has gone stale — which is exactly the case on a
    // wake-up, since the user was away while the work ran. A send starting in
    // that window would otherwise be overwritten by this pre-send snapshot,
    // which is the very thing `useMessageSync`'s seed guard exists to prevent.
    // Bail on the conversation moving too, so a reload can't land in a thread
    // the user has since switched away from.
    if (isSendingRef.current || conversationIdRef.current !== cid) return;

    setMessages(
      persisted.map((msg) =>
        convertPersistedToUIMessage<AssistantUIMessage>(msg, (p) => ({
          ...(p.metadata ?? {}),
          createdAt: p.createdAt,
        }))
      )
    );
    // Reads `isSending` through a ref so this callback is stable: it is in the
    // dep list of the host's wake subscription, and an identity that changed on
    // every streamed turn would churn that subscription for no reason.
  }, [queryClient, setMessages]);

  // Unify stop: the assistant is either waiting on the model or driving the
  // page, and either one must be interruptible from the same button.
  const handleStopChat = React.useCallback(() => {
    core.stop();
    guide.stop();
  }, [core.stop, guide.stop]);

  return {
    pageContext,
    conversationId,
    messages: core.messages,
    chatInput: core.input,
    setChatInput: core.setInput,
    status: core.status,
    isSending: core.isSending,
    isPreparing: conversationQuery.isLoading || messagesQuery.isLoading,
    handleSendMessage: core.sendMessage,
    handleStopChat,
    switchConversation,
    startNewChat,
    sendUserTurn,
    reloadMessages,
    announceAttachment,
  };
}
